import { mockState } from '@/api/mock-data'
import { USE_MOCK } from '@/config'
import { createClient } from '@/api/supabase'
import type { FeedEvent } from '@/types/order'
import type { OrderStatus } from '@/types/enums'
import { tickerStatusPhrase } from '@/utils/ticker-event-text'

/**
 * Прототип: период сдвига окна ленты (демо «живого» UI). Синхронно с refetchInterval в EventTicker.
 */
export const MOCK_FEED_ROTATION_MS = 8000

/** Сколько последних событий тикер берёт с бэка. EventTicker дальше режет до MAX_CHIPS. */
const FEED_LIMIT = 50

type StatusHistoryRowRaw = {
  id: string
  order_id: string
  to_status: string
  changed_at: string
  orders:
    | { brand: string | null; model: string | null }
    | Array<{ brand: string | null; model: string | null }>
    | null
}

function buildSummary(
  to_status: string,
  brand: string | null,
  model: string | null,
): string {
  const phrase = tickerStatusPhrase(to_status as OrderStatus)
  const left = [brand, model].filter(Boolean).join(' ').trim()
  if (left && phrase) return `${left}: ${phrase}`
  if (phrase) return phrase
  return to_status
}

export async function listFeedEvents(): Promise<FeedEvent[]> {
  if (USE_MOCK) {
    const sorted = [...mockState.events].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
    const n = sorted.length
    if (n === 0) return []

    let windowSize = Math.min(50, n)
    let maxStart = Math.max(0, n - windowSize)
    if (maxStart === 0 && n > 1) {
      windowSize = Math.max(1, Math.min(n - 1, 10))
      maxStart = n - windowSize
    }

    const tick = Math.floor(Date.now() / MOCK_FEED_ROTATION_MS)
    const start = maxStart === 0 ? 0 : tick % (maxStart + 1)
    return sorted.slice(start, start + windowSize)
  }

  // Реальный бэк: берём последние N переходов статусов из status_history,
  // подтягиваем марку/модель заказа через FK-embed. Триггер
  // orders_journal_* пишет в эту таблицу на каждый INSERT/UPDATE.status
  // заказа + туда же INSERT из RPC change_order_status. «Живое» обновление
  // обеспечивает useOrdersRealtime (подписка на INSERT в status_history +
  // invalidate ['feed-events']).
  const supabase = createClient()
  const { data, error } = await supabase
    .from('status_history')
    .select('id, order_id, to_status, changed_at, orders(brand, model)')
    .order('changed_at', { ascending: false })
    .limit(FEED_LIMIT)

  if (error) throw new Error(error.message)

  const rows = (data ?? []) as unknown as StatusHistoryRowRaw[]
  return rows.map((row) => {
    const o = Array.isArray(row.orders) ? row.orders[0] : row.orders
    return {
      id: row.id,
      order_id: row.order_id,
      created_at: row.changed_at,
      status: row.to_status as OrderStatus,
      summary: buildSummary(
        row.to_status,
        o?.brand ?? null,
        o?.model ?? null,
      ),
    }
  })
}
